'use client'

import Link from 'next/link'
import { useRef, useState, useTransition } from 'react'
import { cn, Input, Label, TextArea, TextField, toast } from '@heroui/react'

import { ContactMethodPicker } from '@/components/leads/contact-method-picker'
import { LeadSuccess } from '@/components/leads/lead-success'
import { VehiclePicker, type VehicleValue } from '@/components/leads/vehicle-picker'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { CONTACT, CONTACT_PATHS } from '@/config/contact'
import { TurnstileWidget } from '@/components/forms/turnstile-widget'
import { submitWarrantyClaim } from '@/lib/services/actions/warranty-claim'
import type { InquiryContactMethod } from '@/lib/validation/input/inquiry-schema'
import {
  WARRANTY_ISSUE_LABELS,
  WARRANTY_ISSUE_TYPES,
  WARRANTY_PHOTO_LIMITS,
  type WarrantyIssueTypeValue,
} from '@/lib/validation/input/warranty-claim-schema'

interface WarrantyClaimFormProps {
  locationId: string
  defaultName?: string
  defaultEmail?: string
}

const ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif'

export function WarrantyClaimForm({ locationId, defaultName = '', defaultEmail = '' }: WarrantyClaimFormProps) {
  const [isPending, startTransition] = useTransition()
  const [submitted, setSubmitted] = useState<string | null>(null)
  const inFlightRef = useRef(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [name, setName] = useState(defaultName)
  const [email, setEmail] = useState(defaultEmail)
  const [phone, setPhone] = useState('')
  const [contactMethod, setContactMethod] = useState<InquiryContactMethod>('email')
  const [vehicle, setVehicle] = useState<VehicleValue>({ year: '', make: '', model: '' })
  const [licencePlate, setLicencePlate] = useState('')
  const [installedOn, setInstalledOn] = useState('')
  const [jobReference, setJobReference] = useState('')
  const [issueType, setIssueType] = useState<WarrantyIssueTypeValue>('EDGE_LIFTING')
  const [description, setDescription] = useState('')
  const [photos, setPhotos] = useState<File[]>([])
  const [website, setWebsite] = useState('')
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  // Bumped after a rejected submit: the token is single-use, so the widget has
  // to draw a fresh one before the customer can try again.
  const [turnstileNonce, setTurnstileNonce] = useState(0)
  // Mirrors the widget's own check (turnstile-widget.tsx): unconfigured (no
  // site key, e.g. unit tests or a dev box that never set it), the widget
  // renders nothing and reports a null token forever, so gating on the token
  // in that case would disable the button permanently. Only gate when a real
  // challenge is actually in flight.
  const turnstileEnabled = Boolean(process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY)

  const onPickPhotos = (list: FileList | null) => {
    if (!list) return
    const next = [...photos, ...Array.from(list)].slice(0, WARRANTY_PHOTO_LIMITS.maxFiles)
    const tooBig = next.find((f) => f.size > WARRANTY_PHOTO_LIMITS.maxBytes)
    if (tooBig) {
      toast.danger('Photo too large', {
        description: `${tooBig.name} is over ${WARRANTY_PHOTO_LIMITS.maxBytes / (1024 * 1024)} MB. Try a smaller image.`,
      })
    }
    setPhotos(next.filter((f) => f.size <= WARRANTY_PHOTO_LIMITS.maxBytes))
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (inFlightRef.current) return
    inFlightRef.current = true

    const fd = new FormData()
    fd.set('name', name)
    fd.set('email', email)
    fd.set('phone', phone)
    fd.set('contactMethod', contactMethod)
    if (vehicle.year) fd.set('vehicleYear', vehicle.year)
    fd.set('vehicleMake', vehicle.make)
    fd.set('vehicleModel', vehicle.model)
    fd.set('licencePlate', licencePlate)
    fd.set('installedOn', installedOn)
    fd.set('jobReference', jobReference)
    fd.set('issueType', issueType)
    fd.set('description', description)
    fd.set('shopLocationId', locationId)
    fd.set('website', website)
    if (turnstileToken) fd.set('turnstileToken', turnstileToken)
    for (const p of photos) fd.append('photos', p, p.name)

    startTransition(async () => {
      try {
        const result = await submitWarrantyClaim(fd)
        if (result.success && result.data) {
          toast.success('Claim submitted', { description: 'We reply within one business day.' })
          setSubmitted(result.data.id)
        } else {
          setTurnstileNonce((n) => n + 1)
          toast.danger('Could not submit the claim', { description: result.error || 'Please try again.' })
        }
      } finally {
        inFlightRef.current = false
      }
    })
  }

  if (submitted) {
    return (
      <LeadSuccess
        title="Claim received"
        actions={
          <a href={`tel:${CONTACT.phoneTel}`}>
            <Button variant="outline">Call the shop</Button>
          </a>
        }
      >
        <p>
          Your reference is <span className="font-mono font-semibold text-foreground">{submitted}</span>. Keep it for any
          follow-up.
        </p>
        <p>
          A person reviews every claim and replies within one business day, usually to book an inspection at the shop.
          Where the defect is ours, we correct it at no charge.
        </p>
        <p>
          Reporting inside 14 days of installation keeps you inside the workmanship warranty. This submission is your
          written notice, timestamped.
        </p>
      </LeadSuccess>
    )
  }

  return (
    <div className="rounded-2xl border border-border bg-surface p-6 sm:p-8">
      <form onSubmit={handleSubmit} className="space-y-6" aria-label="Warranty claim form">
        {/* The problem */}
        <fieldset className="space-y-3">
          <legend className="text-sm font-medium text-foreground">What is happening?</legend>
          <div role="radiogroup" aria-label="Type of issue" className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {WARRANTY_ISSUE_TYPES.map((t) => {
              const active = t === issueType
              return (
                <button
                  key={t}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setIssueType(t)}
                  data-testid={`issue-${t}`}
                  className={cn(
                    'min-h-11 rounded-xl border px-4 py-2.5 text-left text-sm font-medium transition-colors',
                    active
                      ? 'border-accent bg-accent text-accent-foreground'
                      : 'border-border bg-background text-foreground hover:border-foreground/30'
                  )}
                >
                  {WARRANTY_ISSUE_LABELS[t]}
                </button>
              )
            })}
          </div>
        </fieldset>

        <TextField value={description} onChange={setDescription} isRequired>
          <Label>Describe it</Label>
          <TextArea
            variant="outline"
            rows={5}
            placeholder="Where on the vehicle, when you first noticed it, and anything that happened around then (car wash, pressure washer, collision, extreme cold)."
          />
        </TextField>

        {/* Photos */}
        <div className="space-y-2">
          <span className="block text-sm font-medium text-foreground">
            Photos <span className="font-normal text-muted">(up to {WARRANTY_PHOTO_LIMITS.maxFiles}, strongly recommended)</span>
          </span>
          <label
            className={cn(
              'flex min-h-28 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-border bg-background p-4 text-center transition-colors hover:border-foreground/40',
              photos.length >= WARRANTY_PHOTO_LIMITS.maxFiles && 'pointer-events-none opacity-60'
            )}
          >
            <Icon icon="camera" className="text-2xl text-muted" aria-hidden="true" />
            <span className="text-sm font-semibold text-foreground">
              {photos.length ? 'Add another photo' : 'Tap to add photos'}
            </span>
            <span className="text-xs text-muted">One close-up of the problem, one of the whole panel. JPEG, PNG, WebP, or HEIC.</span>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT}
              multiple
              capture="environment"
              className="sr-only"
              onChange={(e) => onPickPhotos(e.target.files)}
              aria-label="Add photos"
            />
          </label>
          {photos.length > 0 && (
            <ul className="flex flex-wrap gap-2" aria-label="Selected photos">
              {photos.map((p, i) => (
                <li key={`${p.name}-${i}`} className="flex items-center gap-2 rounded-full border border-border bg-background py-1 pl-3 pr-1 text-xs text-foreground">
                  <span className="max-w-[10rem] truncate">{p.name}</span>
                  <button
                    type="button"
                    aria-label={`Remove ${p.name}`}
                    onClick={() => setPhotos(photos.filter((_, j) => j !== i))}
                    className="flex h-6 w-6 items-center justify-center rounded-full text-muted hover:bg-surface hover:text-foreground"
                  >
                    <Icon icon="cw-close" size="sm" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* The job */}
        <div className="space-y-4 border-t border-border pt-6">
          <div className="space-y-1.5">
            <span className="block text-sm font-medium text-foreground">Vehicle</span>
            <VehiclePicker value={vehicle} onChange={setVehicle} />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <TextField value={licencePlate} onChange={setLicencePlate}>
              <Label>Licence plate</Label>
              <Input variant="outline" autoCapitalize="characters" />
            </TextField>
            <TextField value={installedOn} onChange={setInstalledOn}>
              <Label>Installed on</Label>
              <Input type="date" variant="outline" max={new Date().toISOString().slice(0, 10)} />
            </TextField>
            <TextField value={jobReference} onChange={setJobReference}>
              <Label>Invoice or booking #</Label>
              <Input variant="outline" placeholder="If you have it" />
            </TextField>
          </div>
        </div>

        {/* You */}
        <div className="space-y-4 border-t border-border pt-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField value={name} onChange={setName} isRequired>
              <Label>Your name</Label>
              <Input variant="outline" autoComplete="name" />
            </TextField>
            <TextField value={email} onChange={setEmail} isRequired>
              <Label>Email</Label>
              <Input type="email" variant="outline" autoComplete="email" />
            </TextField>
          </div>
          <div className="space-y-2">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <TextField value={phone} onChange={setPhone} isRequired className="flex-1">
                <Label>Phone</Label>
                <Input type="tel" inputMode="tel" variant="outline" autoComplete="tel" />
              </TextField>
              <ContactMethodPicker value={contactMethod} onChange={setContactMethod} />
            </div>
            <p className="text-xs text-muted">We usually need to book an inspection; how should we reach you?</p>
          </div>
        </div>

        <div className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
          <label>
            Website
            <input type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
          </label>
        </div>

        <p className="text-xs text-muted">
          Submitting this form is written notice under the{' '}
          <Link href={CONTACT_PATHS.warrantyPolicy} className="underline hover:text-foreground">
            Limited Warranty
          </Link>
          . It does not guarantee coverage: we inspect first, and material failures go to the film manufacturer.
        </p>

        <TurnstileWidget onToken={setTurnstileToken} resetSignal={turnstileNonce} />

        <Button
          type="submit"
          variant="primary"
          size="lg"
          className="w-full"
          isLoading={isPending}
          isDisabled={isPending || (turnstileEnabled && !turnstileToken)}
        >
          {isPending ? 'Submitting...' : 'Submit claim'}
        </Button>
      </form>
    </div>
  )
}
